import { Box, Button, CircularProgress } from '@mui/material';
import { DataGridPremium, GridColDef, GridRowParams, itIT } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { AutocompleteAdapter, EnhancedForm, IdentityField } from 'components/enhanced/form';
import { required } from 'components/helpers/validators';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionDescription } from 'shared/types/general';
import { normalizePWRows } from './TadPWUtils';
import { ITabTableLayerRow } from './types';
import { productService } from 'shared/services';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import ConfirmationDeleteTableDialog from 'components/ui/feedback/ConfirmationGridDelete';

interface ITabTablePWLayerParams {
  title: string;
  addElementLabel: string;
  selectElementLabel: string;
  currentElement: any;
  disableEdit?: boolean;
  isLoading?: boolean;
  onSubmit: (elementIds: number[]) => void;
}

function TabTablePWLayer(params: ITabTablePWLayerParams) {
  const { t } = useTranslation();
  const {
    title,
    addElementLabel,
    selectElementLabel,
    currentElement,
    disableEdit,
    isLoading,
    onSubmit
  } = params;

  const [products, setProducts] = useState<OptionDescription[]>([]);
  const [warehouses, setWarehouses] = useState<OptionDescription[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [rows, setRows] = useState<ITabTableLayerRow[]>([]);
  const formId = `add-tab-layer-${title.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  useEffect(() => {
    if (!selectedWarehouseId) {
      setProducts([]);
      return;
    }
    productService.getAllBaseInformation({ warehouse_id: selectedWarehouseId }).then(setProducts);
  }, [selectedWarehouseId]);

  useEffect(() => {
    const selectedProductWarehouses = currentElement?.product_warehouses ?? [];
    setRows(normalizePWRows(selectedProductWarehouses, products));
  }, [currentElement, products]);

  const submitRows = (nextRows: ITabTableLayerRow[]) => {
    const nextIds = nextRows.map((row) => Number(row.id)).filter((id) => !Number.isNaN(id));
    onSubmit(nextIds);
    setRows(nextRows);
  };

  const handleAddElement = (values) => {
    productWarehouseService
      .getAllBaseInformation({
        product_id: values.product,
        warehouse_id: values.warehouse
      })
      .then((data) => {
        const selectedOption = data[0];
        const selectedId = selectedOption.id;
        const nextRows = [
          ...rows,
          {
            id: selectedId
          }
        ];
        submitRows(nextRows);
      });
  };

  const handleDelete = (id: number | string) => {
    const nextRows = rows.filter((row) => row.id !== id);
    submitRows(nextRows);
  };

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'product',
        headerName: t('global.code'),
        flex: 1,
        minWidth: 150
      },
      {
        field: 'description',
        headerName: t('global.description'),
        flex: 2,
        minWidth: 240
      },
      {
        field: 'warehouse',
        headerName: t('global.warehouse'),
        flex: 2,
        minWidth: 150
      }
    ];

    if (disableEdit) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        getActions: (params: GridRowParams) => {
          const actions = [];
          actions.push(
            <ConfirmationDeleteTableDialog
              id={+params.row.id}
              handleDelete={() => handleDelete(params.row.id)}
            />
          );
          return actions;
        }
      }
    ];
  }, [disableEdit, rows, t]);

  return (
    <div>
      <EnhancedAccordion title={title} subtitle={addElementLabel}>
        <EnhancedForm
          onSubmit={handleAddElement}
          formId={formId}
          autoStyleLayout={true}
          columns={2}
          costumControls={
            <Button type="submit" form={formId} variant="outlined" fullWidth>
              {t('global.add')}
            </Button>
          }>
          <IdentityField
            name="warehouse"
            component={AutocompleteAdapter}
            options={warehouses}
            label={t('global.warehouse')}
            type="text"
            validate={required}
            setValue={(value) => setSelectedWarehouseId(value ? Number(value.id) : null)}
            required
          />
          <IdentityField
            name="product"
            component={AutocompleteAdapter}
            options={products}
            label={t('global.product')}
            type="text"
            disabled={!selectedWarehouseId}
            optionLabel={'description'}
            required
          />
        </EnhancedForm>
      </EnhancedAccordion>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPremium
          density="compact"
          rows={rows}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          sx={{ border: 'none' }}
          columns={columns}
          pageSizeOptions={[5, 10, 20, 50, 100]}
        />
      )}
    </div>
  );
}

export default TabTablePWLayer;
